import { getAccountLevelCompactLabel } from "../../../Utils/Helper/levelUtils";

export const ASSESSMENT_LEVEL_AL = "AL";
export const ASSESSMENT_LEVEL_RL = "RL";

/**
 * Resolve assessment level from API row (falls back to level code prefix).
 */
export function resolveAssessmentLevel(level) {
  if (!level) return ASSESSMENT_LEVEL_AL;
  if (
    level.assessmentLevel === ASSESSMENT_LEVEL_RL ||
    level.assessment_level === ASSESSMENT_LEVEL_RL
  ) {
    return ASSESSMENT_LEVEL_RL;
  }
  if (
    level.assessmentLevel === ASSESSMENT_LEVEL_AL ||
    level.assessment_level === ASSESSMENT_LEVEL_AL
  ) {
    return ASSESSMENT_LEVEL_AL;
  }
  const code = String(level.levels || level.level_code || "");
  if (/^RL\d+$/i.test(code)) return ASSESSMENT_LEVEL_RL;
  if (/^L\d+$/i.test(code)) return ASSESSMENT_LEVEL_AL;
  return ASSESSMENT_LEVEL_AL;
}

export function isAlOrganizationLevel(level) {
  return resolveAssessmentLevel(level) === ASSESSMENT_LEVEL_AL;
}

export function isRlOrganizationLevel(level) {
  return resolveAssessmentLevel(level) === ASSESSMENT_LEVEL_RL;
}

export function filterOrganizationLevels(levels, assessmentLevel) {
  if (!assessmentLevel) return levels || [];
  return (levels || []).filter(
    level => resolveAssessmentLevel(level) === assessmentLevel
  );
}

function parseLevelSortKey(levelCode) {
  const code = String(levelCode || "");
  const rlMatch = code.match(/^RL(\d+)$/i);
  if (rlMatch) return { group: 1, num: parseInt(rlMatch[1], 10) };
  const alMatch = code.match(/^L(\d+)$/i);
  if (alMatch) return { group: 0, num: parseInt(alMatch[1], 10) };
  return { group: 2, num: 0 };
}

export function sortOrganizationLevels(levels) {
  return [...(levels || [])].sort((a, b) => {
    const aKey = parseLevelSortKey(a.levels || a.level_code);
    const bKey = parseLevelSortKey(b.levels || b.level_code);
    if (aKey.group !== bKey.group) return aKey.group - bKey.group;
    return aKey.num - bKey.num;
  });
}

export function partitionOrganizationLevels(levels) {
  const sorted = sortOrganizationLevels(levels);
  return {
    alLevels: sorted.filter(isAlOrganizationLevel),
    rlLevels: sorted.filter(isRlOrganizationLevel),
  };
}

export function parseRlLevelNumber(levelCode) {
  const code = String(levelCode || "");
  const rlMatch = code.match(/^RL(\d+)$/i);
  if (rlMatch) return Number.parseInt(rlMatch[1], 10);
  const lMatch = code.match(/^L(\d+)$/i);
  return lMatch ? Number.parseInt(lMatch[1], 10) : null;
}

/**
 * Build RL index maps from organization_levels rows (assessment_level = RL).
 * namesByLevel: RL0 → name (e.g. "Unknown")
 * descriptionsByLevel: RL0 → description (coaching / Zappy copy)
 */
export function buildRlLevelLookupMaps(rlLevels) {
  const namesByLevel = {};
  const descriptionsByLevel = {};
  const guidanceByLevel = {};

  for (const row of rlLevels || []) {
    const num = parseRlLevelNumber(row.levels || row.level_code);
    if (num === null) continue;
    namesByLevel[num] = row.name || "";
    descriptionsByLevel[num] = row.description || "";
    guidanceByLevel[num] = (row.guidance || "").trim();
  }

  return { namesByLevel, descriptionsByLevel, guidanceByLevel };
}

export const DEFAULT_RL_RATIONALE_GUIDANCE =
  "Provide your rationale and key evidence that supports this relationship level.";

export function resolveRlRationaleGuidance(guidanceByLevel, rlLevel) {
  if (rlLevel === null || rlLevel === undefined) return "";
  const num = Number(rlLevel);
  if (Number.isNaN(num)) return DEFAULT_RL_RATIONALE_GUIDANCE;
  return guidanceByLevel?.[num] || DEFAULT_RL_RATIONALE_GUIDANCE;
}

/**
 * Goal-target dropdown options: RL 1 — {name}, … (RL0 excluded — goals are forward-looking).
 * @param {Record<number, string>} namesByLevel - from buildRlLevelLookupMaps
 * @param {{ minLevel?: number }} [options]
 */
export function buildRlGoalLevelOptions(namesByLevel, { minLevel = 1 } = {}) {
  return Object.keys(namesByLevel || {})
    .map(key => Number.parseInt(key, 10))
    .filter(num => !Number.isNaN(num) && num >= minLevel)
    .sort((a, b) => a - b)
    .map(num => ({
      value: num,
      label: formatRlGoalLabel(num, namesByLevel),
    }));
}

export function formatRlGoalLabel(rlLevel, namesByLevel) {
  const num = Number(rlLevel);
  if (Number.isNaN(num)) return "";
  const name = namesByLevel?.[num];
  return name ? `RL ${num} — ${name}` : `RL ${num}`;
}

/**
 * Dropdown whose options are org maturity codes (L0–L6 or RL0–RL5).
 */
export function isMaturityLevelDropdownQuestion(question) {
  if (!question || question.type !== "dropdown") return false;
  if (question.id === "maturity_level") return true;
  const options = question.options || [];
  return options.some(opt => {
    const value = String(opt?.value || "");
    return /^L\d+$/i.test(value) || /^RL\d+$/i.test(value);
  });
}

export function buildAlMaturityOptions(alLevels) {
  return (alLevels || []).map(level => ({
    label: getAccountLevelCompactLabel(level.levels, level.name),
    value: level.levels,
  }));
}

/**
 * Replace maturity dropdown options with current AL org levels (strips stale RL rows).
 */
export function sanitizeTemplateQuestionsForAl(questions, alLevels) {
  const alOptions = buildAlMaturityOptions(alLevels);
  if (!Array.isArray(questions) || alOptions.length === 0)
    return questions || [];

  return questions.map(question => {
    if (!isMaturityLevelDropdownQuestion(question)) return question;
    return { ...question, options: alOptions };
  });
}
